import { useState } from 'react'
import { Badge } from './badge'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Loader2, Server, AlertCircle, CheckCircle2, Clock, Database } from 'lucide-react'
import { cn } from '@/utils/tailwind-util'
import type { HealthCheckResponse } from '@/rest/modules/health-api'

interface ServiceStatusBadgeProps {
  status?: HealthCheckResponse
  isLoading: boolean
  error?: string | null
}

function formatResponseTime(ms: number) {
  if (ms < 10) return `${ms}ms`
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatLastChecked(timestamp: number) {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function ServiceIndicator({ healthy, label, icon: Icon, responseTime, lastChecked, error }: {
  healthy: boolean
  label: string
  icon: React.ElementType
  responseTime: number
  lastChecked: number
  error?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer",
          healthy
            ? "border-success/20 bg-success/5 hover:bg-success/10"
            : "border-destructive/20 bg-destructive/5 hover:bg-destructive/10"
        )}>
          {healthy ? (
            <CheckCircle2 className="h-4 w-4 text-success" />
          ) : (
            <AlertCircle className="h-4 w-4 text-destructive" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">{label}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={cn(
                "text-xs",
                healthy ? "text-success" : "text-destructive"
              )}>
                {healthy ? 'Operativo' : 'No disponible'}
              </span>
              <span className="text-xs text-muted-foreground">
                · {formatResponseTime(responseTime)}
              </span>
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {healthy ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : (
              <AlertCircle className="h-4 w-4 text-destructive" />
            )}
            <span className="font-semibold text-sm">{label}</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Estado</span>
              <Badge
                variant={healthy ? 'success' : 'destructive'}
                className="text-xs"
              >
                {healthy ? 'Operativo' : 'No disponible'}
              </Badge>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Tiempo de respuesta</span>
              <span className="font-medium">{formatResponseTime(responseTime)}</span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Última verificación</span>
              <span className="font-medium">{formatLastChecked(lastChecked)}</span>
            </div>
          </div>

          {error && (
            <div className="pt-2 border-t">
              <p className="text-xs text-destructive font-medium mb-1">Error:</p>
              <p className="text-xs text-muted-foreground font-mono bg-muted p-2 rounded break-all">
                {error}
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function AfipServiceDetails({ servers, lastChecked, healthy, error }: {
  servers?: { appServer: string; dbServer: string; authServer: string }
  lastChecked: number
  healthy: boolean
  error?: string
}) {
  const [open, setOpen] = useState(false)

  if (!servers) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer",
            healthy
              ? "border-success/20 bg-success/5 hover:bg-success/10"
              : "border-destructive/20 bg-destructive/5 hover:bg-destructive/10"
          )}>
            {healthy ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : (
              <AlertCircle className="h-4 w-4 text-destructive" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Server className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-medium">AFIP</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={cn(
                  "text-xs",
                  healthy ? "text-success" : "text-destructive"
                )}>
                  {healthy ? 'Operativo' : 'No disponible'}
                </span>
              </div>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {healthy ? (
                <CheckCircle2 className="h-4 w-4 text-success" />
              ) : (
                <AlertCircle className="h-4 w-4 text-destructive" />
              )}
              <span className="font-semibold text-sm">Estado del servicio AFIP</span>
            </div>
            <p className="text-xs text-muted-foreground">
              No hay información detallada disponible.
            </p>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer",
          healthy
            ? "border-success/20 bg-success/5 hover:bg-success/10"
            : "border-destructive/20 bg-destructive/5 hover:bg-destructive/10"
        )}>
          {healthy ? (
            <CheckCircle2 className="h-4 w-4 text-success" />
          ) : (
            <AlertCircle className="h-4 w-4 text-destructive" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Server className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">AFIP</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={cn(
                "text-xs",
                healthy ? "text-success" : "text-destructive"
              )}>
                {healthy ? 'Operativo' : 'No disponible'}
              </span>
              {lastChecked && (
                <span className="text-xs text-muted-foreground">
                  · {formatLastChecked(lastChecked)}
                </span>
              )}
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {healthy ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : (
              <AlertCircle className="h-4 w-4 text-destructive" />
            )}
            <span className="font-semibold text-sm">Estado del servicio AFIP</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Estado general</span>
              <Badge
                variant={healthy ? 'success' : 'destructive'}
                className="text-xs"
              >
                {healthy ? 'Operativo' : 'No operativo'}
              </Badge>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <Server className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Servidor de aplicación</span>
                </div>
                <span className={cn(
                  "font-medium",
                  servers.appServer === 'OK' ? 'text-success' : 'text-destructive'
                )}>
                  {servers.appServer === 'OK' ? 'Operativo' : 'No disponible'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <Server className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Servidor de base de datos</span>
                </div>
                <span className={cn(
                  "font-medium",
                  servers.dbServer === 'OK' ? 'text-success' : 'text-destructive'
                )}>
                  {servers.dbServer === 'OK' ? 'Operativo' : 'No disponible'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <Server className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Servidor de autenticación</span>
                </div>
                <span className={cn(
                  "font-medium",
                  servers.authServer === 'OK' ? 'text-success' : 'text-destructive'
                )}>
                  {servers.authServer === 'OK' ? 'Operativo' : 'No disponible'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-2 border-t">
            <Clock className="h-3 w-3" />
            <span>Última verificación: {formatLastChecked(lastChecked)}</span>
          </div>

          {error && (
            <div className="pt-2 border-t">
              <p className="text-xs text-destructive font-medium mb-1">Error:</p>
              <p className="text-xs text-muted-foreground font-mono bg-muted p-2 rounded break-all">
                {error}
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function ServiceStatusBadge({ status, isLoading, error }: ServiceStatusBadgeProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Verificando servicios...</span>
      </div>
    )
  }

  if (error && !status) {
    return (
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <span className="text-sm text-destructive">Error al verificar servicios</span>
      </div>
    )
  }

  if (!status) {
    return (
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Sin datos de servicios</span>
      </div>
    )
  }

  const { services, timestamp } = status
  const postgresHealthy = services.postgres.healthy
  const afipHealthy = services.afip.healthy
  const allHealthy = postgresHealthy && afipHealthy

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {allHealthy ? (
          <CheckCircle2 className="h-5 w-5 text-success" />
        ) : (
          <AlertCircle className="h-5 w-5 text-destructive" />
        )}
        <span className="text-sm font-medium">
          {allHealthy ? 'Todos los servicios operativos' : 'Algunos servicios no operativos'}
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <ServiceIndicator
          healthy={postgresHealthy}
          label="PostgreSQL"
          icon={Database}
          responseTime={services.postgres.responseTime}
          lastChecked={services.postgres.lastChecked}
          error={services.postgres.error}
        />

        <AfipServiceDetails
          servers={services.afip.servers}
          lastChecked={services.afip.lastChecked}
          healthy={afipHealthy}
          error={services.afip.error}
        />
      </div>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-2 border-t">
        <Clock className="h-3 w-3" />
        <span>Última verificación: {formatLastChecked(timestamp)}</span>
      </div>
    </div>
  )
}
